// Assessment-rendering types, ported from stylos's SurveyPage. A screener is a group
// of questions shown one at a time; `Conditions` let an answer short-circuit or
// jump within the group (backend will eventually own this ordering).

export type QuestionChoice = {
  text: string;
  /** Present on pre-diabetes / phq-gad; used by Conditions. */
  order?: number;
  /** The answer's resource URI (the value submitted). Absent on some screeners. */
  surveyChoiceUrn?: string;
  /**
   * Optional icon URL for the choice (e.g. dynamic-assessment health goals send
   * `imageLink`). Rendered as a leading avatar when present; absent on scale /
   * yes-no screeners, which show text-only rows.
   */
  imageLink?: string;
  /**
   * `true` → this choice is mutually exclusive with every other choice on the
   * same (multi-select) question — e.g. health_conditions' "None of these
   * conditions". Selecting it clears the rest of the selection; selecting any
   * other choice clears a previously-selected exclusive one. Only meaningful
   * when the question is `isMultiSelect`; absent/`false` elsewhere. Submitted
   * as-is — routing in pre_consult questionnaires depends on the raw value, and
   * the backend is responsible for dropping it where it doesn't apply (e.g.
   * health_conditions, at write time).
   */
  exclusive?: boolean;
};

/**
 * `questionType`s that are typed-in free text on the wire — a `freeResponse`
 * (not a choice resourceUri) in the legacy `/surveycustomerresponse` payload,
 * and a `TextField`/`TextArea` (not a choice list) in `AssessmentContent`.
 * Shared by `buildSurveyResponsePayload`'s routing and `AssessmentContent`'s
 * render gate so the two lists can't independently drift — they previously
 * did: `comment` rendered as free text but routed as a choice answer (PR #73
 * review).
 *
 * `date` isn't included even though it's also free-response on the wire:
 * `AssessmentContent` renders it with its own masked input, not a plain
 * `TextField`/`TextArea`, so a caller that needs the on-the-wire set (routing)
 * unions `'date'` in explicitly; a caller that needs the render gate uses
 * this set as-is.
 */
export const FREE_TEXT_QUESTION_TYPES: ReadonlySet<string> = new Set([
  'text',
  'comment',
  'number',
  'timezone',
]);

/**
 * One column of a `repeatingList` question (SurveyJS `matrixdynamic`) — a
 * member-authored, open-ended item, not a picker (e.g. "Medication name",
 * "Dosage" for a current-medications list). `name` becomes the property key
 * on every submitted row object; `title` is the field's label.
 *
 * v1 supports open-ended text cells only, per the "open-ended items" framing
 * in the ticket — the SurveyJS builder can technically author a matrixdynamic
 * column with a choice-based `cellType` (dropdown/checkbox/radiogroup/
 * boolean/comment), but `toDynamicAssessmentQuestions` doesn't read `cellType`
 * and `AssessmentContent` renders every column as a plain text field
 * regardless. That's a deliberate v1 scope cut, not a bug: if a future flow
 * authors a non-text column, it still renders (as a free-text field), it just
 * doesn't get that cell type's picker UI.
 */
export type RepeatingListColumn = {
  name: string;
  title: string;
};

/**
 * One row's answer for a `repeatingList` question — column `name` → the
 * member's typed value for that column, e.g. `{ medication_name: 'Aspirin',
 * dosage: '100mg' }`. Matches SurveyJS's own `matrixdynamic` answer shape
 * (an array of these), which is also the shape the backend needs: verified by
 * reading `SurveySession._persist_answer` / `_answer_value_provided` (no
 * shape validation or transform — a non-string answer value round-trips
 * verbatim into `value_json`), so the storage shape is purely a client
 * convention, and this mirrors SurveyJS's native one rather than inventing a
 * new one.
 */
export type RepeatingListRow = Record<string, string>;

/** Choice value SurveyJS uses for the Other row (`showOtherItem`). */
export const OTHER_CHOICE_VALUE = 'other';

export type AssessmentQuestion = {
  /** The question uuid. */
  key: string;
  order: number;
  /** Page title. */
  text?: string;
  /** Page subtitle / body. */
  additionalInfo?: string;
  /**
   * `'text'` / `'comment'` render a free-text input (single-line / multiline)
   * instead of the choice list — sourced from the SurveyJS element's `type`
   * on dynamic-assessment questions (e.g. reason for visit, treatment goals,
   * goal weight, highest adult weight, and choice→elaboration follow-ups).
   * `'repeatingList'` (SurveyJS `matrixdynamic`) renders open-ended repeating
   * rows with add/remove instead — e.g. current medicines, medical history —
   * for questions with no fixed count and no list to pick from.
   */
  questionType?: 'choice' | 'date' | 'text' | 'comment' | 'repeatingList' | string;
  /**
   * SurveyJS's `inputType` on a `text` element (e.g. `"number"`, `"email"`).
   * Only meaningful when `questionType` is `'text'`; drives the on-screen
   * keyboard (e.g. numeric for goal weight / highest adult weight). Absent on
   * `date` (which already gets its own masked input) and on legacy screeners
   * (which never send `inputType`).
   */
  inputType?: string;
  /**
   * Inclusive earliest allowed date as a local-calendar ISO `YYYY-MM-DD`
   * string, copied from the SurveyJS element's `min` when authored. Absent
   * when the element has no floor; the date validator then uses its default
   * (Jan 1 of last year). Only meaningful when `questionType` is `'date'`.
   */
  minDate?: string;
  /**
   * Inclusive latest allowed date as a local-calendar ISO `YYYY-MM-DD`
   * string, copied from the SurveyJS element's `max` when authored. Absent
   * when the element has no ceiling (no upper bound). Only meaningful when
   * `questionType` is `'date'`.
   */
  maxDate?: string;
  choices: QuestionChoice[];
  /**
   * Column definitions for a `repeatingList` question, from the SurveyJS
   * `matrixdynamic` element's `columns`. Only meaningful when `questionType`
   * is `'repeatingList'`; absent/empty for every other type.
   */
  columns?: RepeatingListColumn[];
  /**
   * Starting row count for a `repeatingList` question, from SurveyJS
   * `matrixdynamic`'s `rowCount`. This is an INITIAL count, not a cap: the
   * member can add further rows without limit (there's no `maxRowCount` on
   * the wire today — if one is added later, enforce it where rows are added,
   * not here). Only meaningful when `questionType` is `'repeatingList'`.
   *
   * Invariant: when set, this is always a positive integer — the only
   * producer (`toDynamicAssessmentQuestions`) clamps a missing/non-positive
   * SurveyJS `rowCount` up to 1. Consumers (`AssessmentContent`) rely on that
   * and don't re-clamp, so this rule lives in exactly one place.
   */
  initialRowCount?: number;
  /**
   * `false` → the question is skippable: Continue with no selection commits
   * an empty answer (`''`) instead of showing the required error. Absent/
   * `true` → required.
   *
   * The "absent" default is source-dependent, resolved by the producer
   * before this reaches AssessmentContent, and the two producers disagree on
   * purpose: legacy screeners (`useAssignedScreener`) never send the flag at
   * all and are always required, so it's left `undefined` there and
   * AssessmentContent's `undefined !== false` check reads that as required.
   * SurveyJS's own convention is the opposite — omitting the flag means
   * *optional* — so `toDynamicAssessmentQuestions` (the converter for every
   * SurveyJS element type, not just `text`/`comment`) resolves an absent flag
   * to explicit `false`: this field is never left `undefined` on questions
   * that came from that converter.
   */
  isRequired?: boolean;
  /**
   * `true` → multiple choices may be selected (a SurveyJS `checkbox` element);
   * the answer is submitted as a `string[]`. Absent/`false` → single-select
   * (radiogroup / scale). Set only by the dynamic-assessment converter — legacy
   * screeners, PHQ/GAD, and pre-diabetes are always single-select.
   */
  isMultiSelect?: boolean;
  /**
   * SurveyJS `showOtherItem` on a checkbox: checking the choice whose value is
   * `"other"` reveals an inline text field. The typed sentence is submitted as
   * `otherKey`, not inside the choice array. Absent/`false` on every other
   * question type and on checkboxes that do not opt in (e.g. health goals'
   * existing `"other"` choice with no inline field).
   */
  showOtherItem?: boolean;
  /** Authored label for the Other row. Converter default is `"Other"`. */
  otherText?: string;
  /** Authored placeholder for the inline Other field. */
  otherPlaceholder?: string;
  /**
   * When true, Continue is blocked if `"other"` is selected and the inline
   * field is blank. Does not make `{name}_other` required when Other is
   * unchecked.
   */
  isOtherRequired?: boolean;
  /**
   * Derived submit key for the typed Other text (`{key}_other`). Set only
   * when `showOtherItem` is true, so AssessmentContent does not re-derive it.
   */
  otherKey?: string;
};

export type Answer = {
  questionKey: string;
  questionValue: string;
};

/**
 * Map of questionKey → chosen value. Single-select questions store a string
 * (choice resource URI / value, or a date string); multi-select (checkbox)
 * questions store a `string[]` of chosen values; `repeatingList` questions
 * store a `RepeatingListRow[]` (one entry per row the member kept).
 */
export type Answers = Record<string, string | string[] | RepeatingListRow[]>;

export type Conditions = Record<
  string,
  Partial<{
    /** Stop the group early if the answer's choice order matches. */
    stopIf: number;
    /** Continue only if the answer's choice order matches; else stop. */
    nextIf: number;
    /** Jump to another question if the answer's choice order matches. */
    jumpToIf: { nextQuestion: number; choice: number };
  }>
>;
